import { useState } from 'react';
import { toast } from 'sonner';
import { useUploadEntityDocument } from '@/api/rpc/hooks/core/useEntityDocuments';
import { repreviewWorkbookSheet } from 'src/components/documents/workbookSheetPreview';
import {
  canSubmitBulkUpload,
  columnsFromSuggestions,
  columnIndex,
  errorMessage,
} from './bulkUploadForm';

export interface MappingWorkbookPreview {
  headers: string[];
  sampleRows: string[][];
  totalDataRows: number;
  sheetNames: string[];
  sheetName: string;
}

export interface BulkUploadResultView {
  createdCount: number;
  mergedCount: number;
  unmatchedCount: number;
  skippedRows: number;
  unmatchedTruncated: boolean;
  unmatched: Array<{
    rowIndex: number;
    partyPart: string;
    internalPart: string;
  }>;
}

interface IngestInput {
  partyId: string;
  documentId: string;
  sheetName?: string;
  partyColumnIndex: number;
  internalColumnIndex: number;
}

export function useBulkMappingUpload<
  TPreview extends MappingWorkbookPreview,
>(args: {
  defaultPartyId?: string;
  entityType: 'ar_customer_product' | 'ap_supplier_product';
  partyColumnKey: string;
  internalColumnKey: string;
  suggestedParty: (preview: TPreview) => number | null;
  suggestedInternal: (preview: TPreview) => number | null;
  previewPending: boolean;
  ingestPending: boolean;
  previewFile: (input: {
    documentId: string;
    sheetName?: string;
  }) => Promise<TPreview>;
  ingestFile: (input: IngestInput) => Promise<BulkUploadResultView>;
  onUploaded: (partyId: string) => void;
  readError: string;
  uploadError: string;
  sheetError: string;
}) {
  const upload = useUploadEntityDocument();
  const [partyId, setPartyId] = useState(args.defaultPartyId);
  const [file, setFile] = useState<File | null>(null);
  const [documentId, setDocumentId] = useState<string | null>(null);
  const [previewData, setPreviewData] = useState<TPreview | null>(null);
  const [sheetName, setSheetName] = useState<string | null>(null);
  const [columns, setColumns] = useState<Record<string, string>>({});
  const [result, setResult] = useState<BulkUploadResultView | null>(null);
  const partyColumn = columns[args.partyColumnKey] ?? '';
  const internalColumn = columns[args.internalColumnKey] ?? '';
  const busy = upload.isPending || args.previewPending || args.ingestPending;
  const canSubmit = canSubmitBulkUpload({
    partyId,
    documentId,
    hasPreview: previewData !== null,
    partyColumn,
    internalColumn,
    busy,
  });

  const applyPreview = (previewResult: TPreview) => {
    setPreviewData(previewResult);
    setSheetName(previewResult.sheetName);
    setColumns(
      columnsFromSuggestions(
        args.partyColumnKey,
        args.internalColumnKey,
        args.suggestedParty(previewResult),
        args.suggestedInternal(previewResult),
      ),
    );
  };

  const clearFile = () => {
    setFile(null);
    setDocumentId(null);
    setPreviewData(null);
    setSheetName(null);
    setColumns({});
  };

  const selectFile = async (selected: File | null) => {
    if (!selected) return;
    setFile(selected);
    try {
      const uploaded = await upload.mutateAsync({
        entityType: args.entityType,
        entityId: crypto.randomUUID(),
        file: selected,
      });
      const previewResult = await args.previewFile({
        documentId: uploaded.documentId,
      });
      setDocumentId(uploaded.documentId);
      applyPreview(previewResult);
    } catch (err) {
      clearFile();
      toast.error(errorMessage(err, args.readError));
    }
  };

  const selectSheet = (nextSheetName: string) =>
    repreviewWorkbookSheet({
      documentId,
      currentSheetName: sheetName,
      nextSheetName,
      preview: args.previewFile,
      onPreview: applyPreview,
      fallbackMessage: args.sheetError,
    });

  const submit = async () => {
    if (!partyId || !documentId || !canSubmit) return;
    try {
      const uploadResult = await args.ingestFile({
        partyId,
        documentId,
        sheetName: sheetName ?? undefined,
        partyColumnIndex: columnIndex(partyColumn),
        internalColumnIndex: columnIndex(internalColumn),
      });
      setResult(uploadResult);
      args.onUploaded(partyId);
    } catch (err) {
      toast.error(errorMessage(err, args.uploadError));
    }
  };

  return {
    partyId,
    setPartyId,
    file,
    previewData,
    sheetName,
    columns,
    setColumn: (key: string, column: string) => {
      setColumns((prev) => ({ ...prev, [key]: column }));
    },
    result,
    busy,
    canSubmit,
    ingestPending: args.ingestPending,
    clearFile,
    selectFile,
    selectSheet,
    submit,
    closeAllowed: (next: boolean) => next || !busy,
  };
}
